import React, { useRef, useLayoutEffect, useState, useEffect, useMemo } from 'react'
import { AnimatePresence, motion, useMotionValue } from 'framer-motion'
import classNames from 'classnames'
import UseCasesItem from '../UseCasesItem/UseCasesItem'
import FeaturedItem from '../FeaturedItem/FeaturedItem'
import styles from './styles.module.scss'
import HeaderMenu from '../HeaderMenu/HeaderMenu'
import { PATH, URL } from '../../../constants/routes'
import { solutionsDropdown, platformDropdown, industryDropdown, resourcesDropdown } from '../../../constants/content'
import { ReactComponent as PlaygroundSecondarySVG } from '../../../img/MenuIcons/PlaygroundSecondarySVG.svg'
import { DropdownId } from '../dropdownId'

interface MenuContainerProps {
  selectedNavOption: DropdownId | null
  selectedNavOptionPosition: { x: number }
  darkMode?: boolean
  whatsNewData?: {
    showWhatsNew?: boolean
    title?: string
    description?: string
    url?: string
  }
  featuredItemData?: {
    title?: string
    linkLabel?: string
    url?: string
    image?: string
  }
}

const MenuContainer = ({
  selectedNavOption,
  darkMode,
  selectedNavOptionPosition,
  whatsNewData,
  featuredItemData,
}: MenuContainerProps) => {
  const platformRef = useRef<HTMLElement | null>(null)
  const solutionsRef = useRef<HTMLElement | null>(null)
  const resourcesRef = useRef<HTMLElement | null>(null)
  const demoRef = useRef<HTMLElement | null>(null)

  const refs = useMemo(
    () => ({
      [DropdownId.Platform]: platformRef,
      [DropdownId.Solutions]: solutionsRef,
      [DropdownId.Resources]: resourcesRef,
      [DropdownId.Demo]: demoRef,
    }),
    []
  )

  const containerWidth = useMotionValue<number | null>(null)
  const containerHeight = useMotionValue<number | null>(null)
  const [prevOption, setPrevOption] = useState<DropdownId | null>(null)
  const [isFirstInteraction, setIsFirstInteraction] = useState(true)
  const menuLeftPosition = useMotionValue(0)

  // Memoize Resources columns separately since they're the most expensive
  const resourcesColumns = useMemo(() => {
    const demoLink = {
      title: 'Playground',
      url: `${URL.hubUrl}playground/`,
      isLocal: false,
      icon: PlaygroundSecondarySVG,
    }
    return [
      // 10 entries (9 + Playground) split evenly 5/5 across the two columns.
      { list: resourcesDropdown.slice(0, 5), centerAligned: true },
      { list: [...resourcesDropdown.slice(5), demoLink], centerAligned: true },
    ]
  }, [])

  // Memoize all column computations to avoid recomputing on every render
  const columnsCache = useMemo(() => {
    return {
      [DropdownId.Platform]: (() => {
        const whatsNewItem = whatsNewData?.showWhatsNew
          ? [
              {
                title: whatsNewData.title ?? "What's New",
                url: whatsNewData.url ?? '#',
                description: whatsNewData.description,
                isNew: true,
              },
            ]
          : []
        return [
          { title: 'Capabilities', list: platformDropdown.integrations },
          { title: 'Platform', list: [...platformDropdown.capabilities, ...whatsNewItem] },
        ]
      })(),
      [DropdownId.Solutions]: [
        { title: 'Protect', list: solutionsDropdown.protect },
        { title: 'Grow', list: solutionsDropdown.grow },
      ],
      [DropdownId.Resources]: resourcesColumns,
    }
  }, [whatsNewData, resourcesColumns])

  useEffect(() => {
    if (selectedNavOption !== null) {
      setIsFirstInteraction(false)
    } else {
      setIsFirstInteraction(true)
    }
  }, [selectedNavOption])

  useLayoutEffect(() => {
    if (!selectedNavOption) {
      return
    }

    const ref = refs[selectedNavOption]
    if (ref && ref.current) {
      const { clientWidth: width, clientHeight: height } = ref.current
      containerWidth.set(width)
      containerHeight.set(height)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNavOption])

  useEffect(() => {
    if (selectedNavOption !== null && selectedNavOption !== prevOption) {
      setPrevOption(selectedNavOption)
    }
  }, [selectedNavOption, prevOption])

  useLayoutEffect(() => {
    if (selectedNavOption && selectedNavOption !== DropdownId.Pricing) {
      const targetPosition =
        selectedNavOption === DropdownId.Demo && selectedNavOptionPosition?.x != null
          ? selectedNavOptionPosition.x + (containerWidth.get() ?? 0)
          : 0
      menuLeftPosition.set(targetPosition)
    }
  }, [selectedNavOption, selectedNavOptionPosition, menuLeftPosition, containerWidth])

  return (
    <AnimatePresence mode='wait'>
      {selectedNavOption !== null && selectedNavOption !== DropdownId.Pricing && (
        <motion.div
          className={styles.menuWrapper}
          style={{
            height: window.innerHeight,
            position: 'absolute',
          }}
          // Translate, NOT scale. Safari sizes an SVG filter's intermediate
          // buffer from the CTM scale in effect the first time it evaluates the
          // filter, and never re-evaluates it — so opening at scale 0.9 left
          // every filtered menu icon (nearly all of them; they're Figma
          // inner-shadow exports with filterUnits="userSpaceOnUse")
          // permanently soft, while text and unfiltered art repainted crisp.
          // A translate keeps the element at 1:1 so the buffers resolve at full
          // resolution. Mirrors apps/web's MegaMenu; don't reintroduce scale.
          initial={{ opacity: 0, y: -4, left: menuLeftPosition.get() }}
          animate={{ opacity: 1, y: 0, left: menuLeftPosition.get() }}
          exit={{ opacity: 0, y: -4, transition: { duration: 0.15 } }}
          transition={{
            duration: 0.2,
            ease: [0.165, 0.84, 0.44, 1],
          }}
        >
          <motion.div
            className={styles.menuBody}
            style={{
              width: containerWidth,
              height: containerHeight,
              transition: isFirstInteraction ? '0' : 'width 0.2s ease, height 0.2s ease',
            }}
          >
            <div className={classNames(styles.menuContent, { [styles.darkMenuContent]: darkMode })}>
              {Object.entries(refs).map(([key, ref]) => (
                <div
                  key={key}
                  className={styles.card}
                  style={{
                    opacity: selectedNavOption === key ? 1 : 0,
                    pointerEvents: selectedNavOption === key ? 'auto' : 'none',
                  }}
                >
                  <HeaderMenu
                    darkMode={darkMode}
                    ref={ref}
                    isOpen
                    parentLabel={DROPDOWN_LABELS[key as DropdownId]}
                    leftColumns={columnsCache[key as DropdownId] || []}
                    leftColumnWidth={getLeftColumnWidth(key)}
                    rightColumn={getRightColumn(key)}
                    rightColumnWidth={getRightColumnWidth(key)}
                    asidePoster={getAsidePoster(key, darkMode, featuredItemData)}
                    bottomLeftComponent={getBottomLeftComponent(key, darkMode)}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const getRightColumn = (key: string) => {
  if (key === DropdownId.Solutions) {
    return { title: 'By Industry', list: industryDropdown }
  }
  return undefined
}

const getRightColumnWidth = (key: string) => {
  if (key === DropdownId.Solutions) {
    return 266
  }
  return undefined
}

const getLeftColumnWidth = (key: string) => {
  if (key === DropdownId.Resources) {
    return 184
  }
  return undefined
}

const getBottomLeftComponent = (key: string, darkMode: boolean | undefined) => {
  if (key === DropdownId.Solutions) {
    return (
      <UseCasesItem
        isDarkMode={darkMode}
        bottomLinkUrl={PATH.useCases}
        bottomLinkText='See all use cases'
        navSection={DROPDOWN_LABELS[DropdownId.Solutions]}
      />
    )
  }
  return undefined
}

const getAsidePoster = (
  key: string,
  darkMode: boolean | undefined,
  featuredItemData?: { title?: string; linkLabel?: string; url?: string; image?: string }
) => {
  if (key === DropdownId.Resources) {
    return (
      <FeaturedItem isDarkMode={darkMode} data={featuredItemData} navSection={DROPDOWN_LABELS[DropdownId.Resources]} />
    )
  }
  return undefined
}

const DROPDOWN_LABELS: Record<DropdownId, string> = {
  [DropdownId.Platform]: 'Product',
  [DropdownId.Solutions]: 'Use Cases',
  [DropdownId.Developers]: 'Developers',
  [DropdownId.Resources]: 'Resources',
  [DropdownId.Pricing]: 'Pricing',
  [DropdownId.Demo]: 'Demo',
  [DropdownId.Docs]: 'Docs',
}

export default MenuContainer
